import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import { AlertCircle } from "lucide-react";

// Valid YouTube video ID = 11 chars, alphanumeric plus "-" and "_"
const YT_ID_RE = /^[A-Za-z0-9_-]{11}$/;
function looksLikeYouTubeId(id) {
  return typeof id === "string" && YT_ID_RE.test(id.trim());
}
// Also accept a full URL and extract the ID (defensive against admin pasting URLs)
function normalizeYouTubeId(raw) {
  if (!raw) return "";
  const t = String(raw).trim();
  if (YT_ID_RE.test(t)) return t;
  const m = t.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/))([A-Za-z0-9_-]{11})/);
  return m ? m[1] : "";
}

export default function WeddingFilms() {
  const [films, setFilms] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get("/films")
      .then(r => setFilms(r.data))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  // Group by whatever category the admin has entered (not a hardcoded list).
  // "Uncategorized" bucket catches any film without a category.
  const byCat = films.reduce((acc, f) => {
    const cat = (f.category || "").trim() || "Uncategorized";
    (acc[cat] ||= []).push(f);
    return acc;
  }, {});
  const orderedCats = Object.keys(byCat).sort((a, b) => a.localeCompare(b));

  return (
    <div data-testid="films-page" className="pt-32">
      <SEO title="Wedding Films" path="/films" description="A collection of wedding films we've made — highlight films, full ceremonies, engagement films and teasers, each shaped around a couple's story." />
      <section className="section-pad pb-12 max-w-[1400px] mx-auto text-center">
        <div className="eyebrow">Wedding Films</div>
        <h1 className="font-serif text-5xl md:text-6xl font-light mt-4 tracking-tight">Films That Bring You <em className="text-[#D4AF37] not-italic">Back</em></h1>
        <p className="text-white/60 mt-4 max-w-2xl mx-auto">Each film is shaped quietly — moments, music, and emotion edited to feel true to the day they were lived.</p>
      </section>

      {loaded && films.length === 0 && (
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 text-center text-white/40 py-16">
          No films published yet.
        </div>
      )}

      {orderedCats.map(cat => (
        <section key={cat} className="pb-24 max-w-[1400px] mx-auto px-6 md:px-10">
          <h2 className="font-serif text-3xl mb-8"><span className="text-[#D4AF37]">·</span> {cat}</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {byCat[cat].map(f => {
              const ytId = normalizeYouTubeId(f.youtube_id);
              const valid = looksLikeYouTubeId(ytId);
              return (
                <div key={f.id} className="bg-[#0a0a0a] border border-white/10" data-testid={`film-card-${f.id}`}>
                  <div className="aspect-video relative">
                    {valid ? (
                      <iframe
                        src={`https://www.youtube.com/embed/${ytId}`}
                        title={f.title}
                        loading="lazy"
                        className="w-full h-full"
                        allow="autoplay; encrypted-media; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center bg-black text-white/50 text-sm gap-2" data-testid={`film-invalid-${f.id}`}>
                        <AlertCircle size={22} className="text-[#D4AF37]" />
                        <span>Video unavailable — invalid YouTube ID</span>
                        {f.youtube_id && (
                          <code className="text-xs text-white/30 mt-1">value: "{f.youtube_id}"</code>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif text-xl">{f.title}</h3>
                    {f.description && <p className="text-sm text-white/55 mt-2">{f.description}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
